import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, PiggyBank, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { Lang } from "@/lib/translations";

interface RoiMiniCalcProps {
  lang: Lang;
}

type SizeOption = { sqft: number; label: string; cost: number };
type UseOption = { value: "rent" | "family"; label: { en: string; es: string } };
type CityOption = { slug: string; name: string; rentPerSqft: number };

const sizeOptions: SizeOption[] = [
  { sqft: 400, label: "400 sqft (JADU)", cost: 130000 },
  { sqft: 600, label: "600 sqft (1BR)", cost: 180000 },
  { sqft: 800, label: "800 sqft (2BR)", cost: 230000 },
  { sqft: 1000, label: "1,000 sqft (2–3BR)", cost: 285000 },
];

// Approximate detached-ADU rent per sqft per month based on 2026 San Diego comps.
const cityOptions: CityOption[] = [
  { slug: "san-diego", name: "San Diego", rentPerSqft: 3.4 },
  { slug: "chula-vista", name: "Chula Vista", rentPerSqft: 2.8 },
  { slug: "san-marcos", name: "San Marcos", rentPerSqft: 2.9 },
  { slug: "carlsbad", name: "Carlsbad", rentPerSqft: 3.6 },
  { slug: "encinitas", name: "Encinitas", rentPerSqft: 3.8 },
  { slug: "la-mesa", name: "La Mesa", rentPerSqft: 3.0 },
  { slug: "poway", name: "Poway", rentPerSqft: 3.1 },
  { slug: "escondido", name: "Escondido", rentPerSqft: 2.7 },
  { slug: "vista", name: "Vista", rentPerSqft: 2.8 },
  { slug: "bonita", name: "Bonita", rentPerSqft: 3.0 },
];

const useOptions: UseOption[] = [
  { value: "rent", label: { en: "Rental income", es: "Ingreso por renta" } },
  { value: "family", label: { en: "Family member", es: "Familiar" } },
];

const fmt = (n: number) =>
  n >= 1000 ? `$${Math.round(n / 100) / 10}k` : `$${Math.round(n).toLocaleString()}`;

const fmtFull = (n: number) => `$${Math.round(n).toLocaleString()}`;

export default function RoiMiniCalc({ lang }: RoiMiniCalcProps) {
  const [sizeIdx, setSizeIdx] = useState(1);
  const [cityIdx, setCityIdx] = useState(0);
  const [use, setUse] = useState<"rent" | "family">("rent");

  const size = sizeOptions[sizeIdx];
  const city = cityOptions[cityIdx];

  const result = useMemo(() => {
    const monthlyRent = Math.round(size.sqft * city.rentPerSqft);
    // Family use = same cost, no rent; but property value uplift stays.
    const effectiveRent = use === "rent" ? monthlyRent : 0;
    const annual = effectiveRent * 12;
    const paybackYears = annual > 0 ? size.cost / annual : Infinity;
    // San Diego appraisers: ~85% of build cost added to home value.
    const valueUplift = Math.round(size.cost * 0.85);
    return { monthlyRent, annual, paybackYears, valueUplift };
  }, [size, city, use]);

  const L = lang === "en"
    ? {
        label: "ADU Calculator",
        title: "See your ADU numbers in 10 seconds",
        desc: "Real San Diego rent comps and 2026 build costs. Nothing to fill out — pick three options.",
        size: "ADU size",
        city: "Your city",
        useLabel: "Primary use",
        monthlyRent: "Est. monthly rent",
        payback: "Payback period",
        uplift: "Est. home value uplift",
        years: "years",
        familyNote: "For family use we don't count rent — but you keep the property value uplift.",
        disclaimer: "Estimates only. Actuals depend on lot, finishes, and lender terms.",
        cta: "Get a written price estimate",
        deep: "Open full ROI calculator",
      }
    : {
        label: "Calculadora ADU",
        title: "Vea los números de su ADU en 10 segundos",
        desc: "Comparables reales de renta en San Diego y costos de construcción 2026. Solo elija tres opciones.",
        size: "Tamaño de ADU",
        city: "Su ciudad",
        useLabel: "Uso principal",
        monthlyRent: "Renta mensual est.",
        payback: "Período de recuperación",
        uplift: "Aumento est. de valor de casa",
        years: "años",
        familyNote: "Para uso familiar no contamos renta — pero conserva el aumento de valor de la propiedad.",
        disclaimer: "Estimados solamente. El actual depende del lote, acabados y términos del prestamista.",
        cta: "Obtener estimado escrito",
        deep: "Abrir calculadora ROI completa",
      };

  return (
    <div className="max-w-5xl mx-auto bg-card border border-border rounded-3xl shadow-xl overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-5">
        {/* Inputs */}
        <div className="lg:col-span-3 p-6 sm:p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-border">
          <span className="inline-block text-primary font-heading font-bold text-xs tracking-widest uppercase">
            {L.label}
          </span>
          <h3 className="mt-2 text-2xl sm:text-3xl font-heading font-extrabold text-foreground leading-snug">
            {L.title}
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">{L.desc}</p>

          <div className="mt-6 space-y-5">
            <div>
              <label htmlFor="mini-size" className="block text-xs font-heading font-bold uppercase tracking-wider text-muted-foreground mb-2">
                {L.size}
              </label>
              <select
                id="mini-size"
                value={sizeIdx}
                onChange={(e) => setSizeIdx(Number(e.target.value))}
                className="w-full h-11 px-4 rounded-xl border border-border bg-background text-foreground font-medium focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {sizeOptions.map((s, i) => (
                  <option key={s.sqft} value={i}>{s.label} — ~{fmtFull(s.cost)}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="mini-city" className="block text-xs font-heading font-bold uppercase tracking-wider text-muted-foreground mb-2">
                {L.city}
              </label>
              <select
                id="mini-city"
                value={cityIdx}
                onChange={(e) => setCityIdx(Number(e.target.value))}
                className="w-full h-11 px-4 rounded-xl border border-border bg-background text-foreground font-medium focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {cityOptions.map((c, i) => (
                  <option key={c.slug} value={i}>{c.name}</option>
                ))}
              </select>
            </div>

            <div>
              <span className="block text-xs font-heading font-bold uppercase tracking-wider text-muted-foreground mb-2">
                {L.useLabel}
              </span>
              <div className="grid grid-cols-2 gap-2">
                {useOptions.map((o) => {
                  const active = use === o.value;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => setUse(o.value)}
                      className={`h-11 rounded-xl border font-heading font-bold text-sm transition-all ${
                        active
                          ? "bg-primary text-primary-foreground border-primary shadow-sm"
                          : "bg-background text-foreground border-border hover:border-primary"
                      }`}
                    >
                      {o.label[lang]}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="lg:col-span-2 p-6 sm:p-8 lg:p-10 bg-gradient-to-br from-primary/8 via-background to-primary/4">
          <div className="space-y-4">
            <div className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <PiggyBank className="h-4 w-4 text-primary" />
                <span className="text-xs font-heading font-bold uppercase tracking-wider">{L.monthlyRent}</span>
              </div>
              <p className="mt-1 text-2xl font-heading font-extrabold text-foreground">
                {use === "rent" ? fmtFull(result.monthlyRent) : "—"}
              </p>
            </div>

            <div className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="h-4 w-4 text-primary" />
                <span className="text-xs font-heading font-bold uppercase tracking-wider">{L.payback}</span>
              </div>
              <p className="mt-1 text-2xl font-heading font-extrabold text-foreground">
                {Number.isFinite(result.paybackYears)
                  ? `${result.paybackYears.toFixed(1)} ${L.years}`
                  : "—"}
              </p>
            </div>

            <div className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <TrendingUp className="h-4 w-4 text-primary" />
                <span className="text-xs font-heading font-bold uppercase tracking-wider">{L.uplift}</span>
              </div>
              <p className="mt-1 text-2xl font-heading font-extrabold text-foreground">
                +{fmt(result.valueUplift)}
              </p>
            </div>
          </div>

          {use === "family" && (
            <p className="mt-3 text-xs text-muted-foreground italic">{L.familyNote}</p>
          )}
          <p className="mt-3 text-[11px] text-muted-foreground">{L.disclaimer}</p>

          <div className="mt-5 flex flex-col gap-2">
            <Link to="/roi-calculator" className="w-full">
              <Button className="w-full h-11 font-heading font-bold bg-primary text-primary-foreground hover:bg-primary/90">
                {L.deep}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
